/**
 * The canonical schemas, and the mapping layer that gets a customer's export
 * into them. SPEC-0001, BP.md §3.
 *
 * BP.md §8 names data normalization as the real technical challenge: every
 * company's CPQ and PSA export has different column names, formats, and
 * quirks. Requiring an exact template is what separates a one-off script from
 * a product, so the canonical field list is *ours* and the customer's headers
 * are mapped onto it.
 */
import { z } from 'zod';
export const SOURCE_TYPES = ['sold', 'budget'];
export const SOLD_FIELDS = [
    { key: 'project_id', label: 'Project ID', required: true, kind: 'text', synonyms: ['project', 'project code', 'proj id', 'project_number'] },
    { key: 'client_name', label: 'Client', required: true, kind: 'text', synonyms: ['client', 'customer', 'account', 'account name'] },
    { key: 'role', label: 'Role', required: true, kind: 'text', synonyms: ['resource role', 'position', 'grade', 'title'] },
    { key: 'sold_hours', label: 'Sold hours', required: true, kind: 'hours', synonyms: ['hours sold', 'sow hours', 'contracted hours', 'qty'] },
    { key: 'rate', label: 'Rate', required: true, kind: 'money', synonyms: ['bill rate', 'hourly rate', 'unit price', 'rate/hr'] },
    { key: 'sow_signed_date', label: 'SOW signed date', required: false, kind: 'date', synonyms: ['sow date', 'signed', 'contract date', 'close date'] },
    // SPEC-0007: distinct from sow_signed_date — a SOW is typically signed at a
    // project's start, not on its go-live or end date.
    { key: 'go_live_date', label: 'Go-live date', required: false, kind: 'date', synonyms: ['go live', 'go-live', 'start date', 'kickoff date'] },
    { key: 'end_date', label: 'End date', required: false, kind: 'date', synonyms: ['end date', 'engagement end', 'wrap date', 'project end'] },
];
export const BUDGET_FIELDS = [
    { key: 'project_id', label: 'Project ID', required: true, kind: 'text', synonyms: ['project', 'project code', 'proj id', 'project_number'] },
    { key: 'client_name', label: 'Client', required: true, kind: 'text', synonyms: ['client', 'customer', 'account', 'account name'] },
    { key: 'role', label: 'Role', required: true, kind: 'text', synonyms: ['resource role', 'position', 'grade', 'title'] },
    { key: 'budgeted_hours', label: 'Budgeted hours', required: true, kind: 'hours', synonyms: ['budget hrs', 'budget hours', 'planned hours', 'baseline hours'] },
    { key: 'hours_consumed', label: 'Hours consumed', required: true, kind: 'hours', synonyms: ['actual hours', 'burned hours', 'used hours', 'actuals'] },
    { key: 'last_updated', label: 'Last updated', required: false, kind: 'date', synonyms: ['updated', 'modified', 'as of', 'snapshot date'] },
];
export function fieldsFor(source) {
    return source === 'sold' ? SOLD_FIELDS : BUDGET_FIELDS;
}
/** Canonical key → the customer's header for that column. */
export const columnMappingSchema = z.record(z.string(), z.string().min(1));
/** A user-confirmed role alias. Never inferred, only suggested. ADR-0010. */
export const roleAliasSchema = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
});
/**
 * A saved mapping profile, so the weekly re-run (BP.md §4) is a two-click
 * operation rather than a re-mapping exercise. SPEC-0001 step 6.
 *
 * A profile belongs to **one source**. The two exports come from different
 * systems — a CPQ and a PSA — and beyond `project_id`, `client_name` and
 * `role` they ask for entirely different fields, so in practice a mapping for
 * one is not a mapping for the other.
 *
 * A side-agnostic version was tried first (2026-08-31) and reversed the same
 * day: sharing three of six columns was not enough to make one profile serve
 * both files, and offering a sold profile when mapping a PSA export mostly
 * offered something that could not fill the fields being asked for.
 *
 * `sourceType` is therefore part of the identity: a name need only be unique
 * within its source, so "Acme" can name both a sold and a budget profile
 * without collision.
 */
export const mappingProfileSchema = z.object({
    id: z.string().optional(),
    name: z.string().trim().min(1),
    sourceType: z.enum(SOURCE_TYPES),
    columns: columnMappingSchema,
    aliases: z.array(roleAliasSchema).default([]),
    updatedAt: z.string().optional(),
});
/**
 * Pre-populate a mapping from the customer's headers.
 *
 * These are SUGGESTIONS the user confirms — never an applied mapping. The
 * distinction is the same one ADR-0010 draws for role aliases: a silently
 * wrong guess in a reconciliation tool is worse than no guess.
 */
/**
 * Pre-fill a side's mapping from a saved profile of that same source, falling
 * back to header guesses for anything the profile does not cover.
 *
 * A profile entry is used **only if the uploaded file actually has that
 * header**. Trusting a profile blindly would map a column that is not present
 * and silently produce empty values — the exact class of silent failure this
 * product exists to catch. The result remains a suggestion the user confirms,
 * just as an unaided guess is (SPEC-0001 step 3).
 */
export function applyProfile(profile, headers, source) {
    const available = new Set(headers);
    const result = suggestMapping(headers, source);
    for (const field of fieldsFor(source)) {
        const saved = profile.columns[field.key];
        if (saved !== undefined && available.has(saved)) {
            result[field.key] = saved;
        }
    }
    return result;
}
export function suggestMapping(headers, source) {
    const key = (s) => s.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
    const suggestion = {};
    const taken = new Set();
    for (const field of fieldsFor(source)) {
        const candidates = [field.key, field.label, ...field.synonyms].map(key);
        const match = headers.find((h) => !taken.has(h) && candidates.includes(key(h)));
        if (match !== undefined) {
            suggestion[field.key] = match;
            taken.add(match);
        }
    }
    return suggestion;
}
//# sourceMappingURL=canonical.js.map